/**
 * Shared Custom Element Registration (Issue #94)
 *
 * Components used by more than one shell (property + user, sometimes
 * guest) live in `static/js/luboo/components/`. Each shell calls
 * `registerSharedElements()` from its own `register-elements.js` so the
 * registration ordering stays under shell control — components don't
 * self-register at module top.
 *
 * The `customElements.get()` guard makes this idempotent across shells.
 */

import { SidebarItemElement } from './sidebar-item.js';
import { LubooDialog } from './luboo-dialog.js';
import { LubooToast, LubooToastQueue } from './luboo-toast.js';
import { SearchableSelectElement } from './searchable-select.js';
import { DateRangePickerElement } from './date-range-picker.js';
import { TopographicIdenticonOrImageElement } from './topographic-identicon-or-image.js';
import { ImageCropper } from './image-cropper.js';
import { AccountTeamList } from './account-team-list.js';
import { CustomRolesEditor } from './custom-roles-editor.js';
import { RoleAuditLog } from './role-audit-log.js';
import { PropertyPermissionsList } from './property-permissions-list.js';
import { LunariaManagementChat } from './lunaria-management-chat.js';
import { VoiceAssistant } from './voice-assistant.js';
import { UriRouterElement } from './uri-router.js';
import { UriRouterNavigationElement } from './uri-router-navigation.js';
import { UriResponsiveElement } from './uri-responsive.js';
import { CopyRightElement } from './copy-right.js';
import { SvgIconElement } from './icons/svg-icon.js';
import { SvgIconSetElement } from './icons/svg-icon-set.js';
import { UserProfileView } from './user-profile-view.js';
import { UserSettingsView } from './user-settings-view.js';

// Side-effect imports — these modules bind to document/window rather
// than register a custom element tag.
import './status-bar-monitor.js';
import './luboo-tooltip.js';

/**
 * Define all shared custom elements.
 *
 * NOTE: tag→class list is built inside the function (NOT at module top
 * level). Some class symbols are `var`-hoisted but their assignment
 * happens later in the bundled script; module-top array literals would
 * capture `undefined` for those (see property/register-elements.js for
 * the full Issue #94 Phase 5 note).
 *
 * Order matters within the list: customElements.define() synchronously
 * upgrades existing matching elements, so dependencies must be defined
 * first (svg-icon-set before svg-icon, queue before toast, etc.).
 */
export function registerSharedElements() {
  const SHARED_ELEMENTS = [
    // Icon foundation (svg-icon's connectedCallback queries for its iconset)
    ['svg-icon-set', SvgIconSetElement],
    ['svg-icon', SvgIconElement],

    // Standalone primitives
    ['copy-right', CopyRightElement],
    ['uri-router', UriRouterElement],
    ['uri-router-navigation', UriRouterNavigationElement],
    ['uri-responsive', UriResponsiveElement],
    ['searchable-select', SearchableSelectElement],
    ['date-range-picker', DateRangePickerElement],
    ['topographic-identicon-or-image', TopographicIdenticonOrImageElement],
    ['image-cropper', ImageCropper],

    // Toast: queue before the individual toast element
    ['luboo-toast-queue', LubooToastQueue],
    ['luboo-toast', LubooToast],

    // Higher-level UI building blocks
    ['luboo-dialog', LubooDialog],
    ['sidebar-item', SidebarItemElement],

    // Composites (use the above)
    ['account-team-list', AccountTeamList],
    ['custom-roles-editor', CustomRolesEditor],
    ['role-audit-log', RoleAuditLog],
    ['property-permissions-list', PropertyPermissionsList],
    ['lunaria-management-chat', LunariaManagementChat],
    ['voice-assistant', VoiceAssistant],
    ['user-profile-view', UserProfileView],
    ['user-settings-view', UserSettingsView],
  ];
  for (const [tag, klass] of SHARED_ELEMENTS) {
    if (!customElements.get(tag)) {
      if (typeof klass !== 'function') {
        console.error(`[register-shared-elements] '${tag}' class is not a function:`, klass);
        continue;
      }
      customElements.define(tag, klass);
    }
  }
}
